import { useEffect, useId, useRef, useState } from "react";
import { HelpCircle } from "lucide-react";
import { GLOSSARY, type GlossaryKey } from "../glossary";

/**
 * "What is this number?"
 *
 * Deliberately NOT hover-only. Hover does not exist on a phone, and this client
 * reads the dashboard on a phone, so an explanation that only appears on hover is
 * an explanation half the readers never see.
 *
 * Hover and keyboard focus are handled in CSS, not here, and that split is the
 * whole point. The first version opened on `onMouseEnter` AND toggled on click —
 * so a tap fired both, opened it and closed it in the same gesture, and the bubble
 * never appeared on touch at all. CSS `@media (hover: hover)` asks the device
 * whether it really has a pointer; JavaScript cannot, and guessing is what broke
 * it. React now owns exactly one thing: the tap.
 *
 * The text comes from `glossary.ts` rather than from props, so the same figure
 * cannot be described one way here and another way on the statement.
 */
export default function Explain({ of, align = "start" }: { of: GlossaryKey; align?: "start" | "end" }) {
  const [open, setOpen] = useState(false);
  const [flipped, setFlipped] = useState(false);
  const wrapRef = useRef<HTMLSpanElement>(null);
  const bubbleRef = useRef<HTMLSpanElement>(null);
  const id = useId();
  const entry = GLOSSARY[of];

  // Close on Escape and on a press anywhere else. Without the outside press an
  // opened bubble follows the reader around the page until they find the button
  // again, which on a phone means it covers the number it was explaining.
  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => { if (event.key === "Escape") setOpen(false); };
    const onDown = (event: MouseEvent | TouchEvent) => {
      if (!wrapRef.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onDown);
    document.addEventListener("touchstart", onDown);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("touchstart", onDown);
    };
  }, [open]);

  // Whether the bubble fits is a question about the live layout, not about which
  // column the card was written into. A static `align` guess was wrong at 768px —
  // one bubble ran 8px past the edge — and any breakpoint tuned to this grid would
  // break the next time the grid changed. An observer asks the browser directly,
  // and it fires for the CSS hover path too, which a click handler never sees.
  useEffect(() => {
    const bubble = bubbleRef.current;
    if (!bubble || typeof IntersectionObserver === "undefined") return;
    const observer = new IntersectionObserver(([entry]) => {
      if (!entry) return;
      if (entry.intersectionRatio === 0) {
        setFlipped(false); // hidden again — start the next open from the natural side
        return;
      }
      // Only ever flip TOWARDS fitting. Flipping back on the same open would let the
      // two states chase each other every time the observer fired.
      if (entry.intersectionRatio < 1 && entry.boundingClientRect.right > window.innerWidth - 8) {
        setFlipped(true);
      }
    }, { threshold: [0, 0.999, 1] });
    observer.observe(bubble);
    return () => observer.disconnect();
  }, []);

  return (
    <span className="explain" ref={wrapRef} data-open={open ? "true" : undefined}>
      <button
        type="button"
        className="explain-button"
        aria-label={`What is ${entry.term.toLowerCase()}?`}
        aria-expanded={open}
        aria-describedby={id}
        onClick={() => setOpen((was) => !was)}
      >
        <HelpCircle size={13} aria-hidden="true" />
      </button>
      {/* Always in the DOM so hover and focus can reveal it without JavaScript.
          `display: none` keeps it out of the accessibility tree until it shows. */}
      <span id={id} role="tooltip" ref={bubbleRef} className={`explain-bubble is-${flipped ? "end" : align}`}>
        <strong>{entry.term}</strong>
        {entry.body}
      </span>
    </span>
  );
}
